import React from 'react';
import styled from 'styled-components';
import { panel } from './panelTheme';

export type PropertiesPanelSegmentedOption = {
    value: string;
    label: React.ReactNode;
};

export type PropertiesPanelSegmentedPropertyProps = {
    options: PropertiesPanelSegmentedOption[];
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    ariaLabel?: string;
    /**
     * A TUTTA RIGA (utente, 2026-10-15; prototipo `seg(..., "fill")`): la scelta che è da sola sotto il
     * suo nome — la direzione di un gruppo — prende la larghezza della card, a scomparti uguali.
     */
    fill?: boolean;
};

// DUE NOMI AFFIANCATI, NON UN INTERRUTTORE.
//
// Un interruttore mostra un nome solo — acceso o spento — e lascia l'altro implicito nello
// spento: va bene quando una cosa si accende, non quando si SCEGLIE fra due. Qui nessuna delle
// due voci è la negazione dell'altra — sono due modi di consegnare la stessa cosa — e vederle
// accanto è ciò che rende il passaggio immediato invece che da indovinare.
//
// STA NELLA TESTATA DELLA CARD e non fra le righe, per la stessa ragione per cui ci sta
// l'interruttore di una famiglia: non è un parametro fra i parametri, cambia la FORMA di ciò che
// c'è sotto. La differenza con quell'interruttore è che questo non accende niente.
//
// LA VESTE È QUELLA DEL PROTOTIPO (`.theme-seg.text.eq` in un Inspector): la pista col tono dei cursori e un filo
// di bordo, gli scomparti larghi uguali — quanto il nome più lungo —, la voce scelta bianca con un'ombra appena.
const Group = styled.div<{ $disabled?: boolean; $fill?: boolean }>`
    display: ${(props) => (props.$fill ? 'flex' : 'inline-grid')};
    grid-auto-flow: column;
    grid-auto-columns: 1fr;
    flex: ${(props) => (props.$fill ? '1 1 auto' : 'none')};
    width: ${(props) => (props.$fill ? '100%' : 'auto')};
    padding: 2px;
    gap: 2px;
    border-radius: 999px;
    background: var(--editor-track, ${panel.field});
    box-shadow: inset 0 0 0 1px ${panel.line};
    opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
`;

const Option = styled.button<{ $active: boolean; $fill?: boolean }>`
    appearance: none;
    border: none;
    cursor: pointer;
    flex: ${(props) => (props.$fill ? '1 1 0' : 'none')};
    min-width: 0;
    height: ${(props) => (props.$fill ? '24px' : '22px')};
    padding: 0 10px;
    border-radius: 999px;
    font: inherit;
    font-size: ${(props) => (props.$fill ? '12px' : '11px')};
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 500;
    white-space: nowrap;
    background: ${(props) => (props.$active ? 'var(--editor-raised, #fff)' : 'transparent')};
    color: ${(props) => (props.$active ? panel.ink : panel.inkMuted)};
    box-shadow: ${(props) => (props.$active ? '0 1px 2px rgba(30, 42, 56, 0.05), 0 0 0 1px rgba(30, 42, 56, 0.06)' : 'none')};
    transition:
        color 140ms ease,
        background 140ms ease,
        box-shadow 140ms ease;

    &:hover:not(:disabled) {
        color: ${panel.ink};
    }

    &:disabled {
        cursor: default;
    }
`;

export const PropertiesPanelSegmented: React.FC<PropertiesPanelSegmentedPropertyProps> = ({ options, value, onChange, disabled, ariaLabel, fill }) => (
    <Group role='radiogroup' aria-label={ariaLabel} $disabled={disabled} $fill={fill}>
        {options.map((option) => (
            <Option
                key={option.value}
                type='button'
                role='radio'
                aria-checked={option.value === value}
                $active={option.value === value}
                $fill={fill}
                disabled={disabled}
                onClick={() => option.value !== value && onChange(option.value)}
            >
                {option.label}
            </Option>
        ))}
    </Group>
);
